import { MenuItem } from '@thyme/nashville/src/types/navbar'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useRouteAuthz } from '@/router/authz'
import { useFlagStore } from '@/store/flags/flags'
import { applyVisibilityDefaults } from './visibility'

/**
 *
 */
export function useNavBarItems() {
  const { checkIfRouteAuthorized } = useRouteAuthz()
  const { showDebugInNavBar, viewUnmatchedVoicemails } = storeToRefs(
    useFlagStore()
  )

  const userManagementItems: MenuItem[] = [
    { label: 'Edit Users', url: '/admin/users' },
    { label: 'Edit Care Pods', url: '/admin/care_pods' },
  ]

  const adminItems: MenuItem[] = [
    { label: 'User Mgmt', items: userManagementItems },
    { label: 'User Queue', url: '/admin/queue' },
    { label: 'Edit Care Plan Goals', url: '/admin/care_plan_goals' },
    {
      label: 'Edit Comorbidity References',
      url: '/admin/comorbidity_reference',
    },
    { label: 'Edit Document Categories', url: '/admin/document_category' },
    {
      label: 'Playbooks',
      items: [
        { label: 'Edit Playbooks', url: '/admin/playbooks/playbooks' },
        { label: 'Edit Tasks', url: '/admin/playbooks/tasks' },
        { label: 'Edit Subtasks', url: '/admin/playbooks/subtasks' },
        { label: 'CSV Uploads', url: '/admin/csv' },
      ],
    },
    { label: 'Triggers', url: '/admin/triggers' },
    {
      label: 'Outbound Georouting',
      items: [
        {
          label: 'Outbound Phone Numbers',
          url: '/admin/thyme_phone_numbers',
        },
        {
          label: 'Outbound Phone Number Configurations',
          url: '/admin/thyme_phone_number_configurations',
        },
        { label: 'Branded Caller ID', url: '/admin/branded_caller_ids' },
      ],
    },
    { label: 'Meos Charges', url: '/admin/meos_charges' },
    { label: 'Express Config', url: '/admin/express_config' },
    { label: 'Design System Sandbox', url: '/sandbox/' },
    // All users have _access_ to the debug page so developers can use it if helping with a user issue,
    // but in most cases it should be hidden (except for the tech team themselves)
    {
      label: 'Debug Console',
      url: '/debug',
      visible: () => showDebugInNavBar.value,
    },
  ]

  const toolsItems: MenuItem[] = [
    { label: 'SOP Library', url: '/tools/library' },
    { label: 'Unknown Texts', url: '/tools/unknown-texts' },
    { label: 'Fax Management', url: '/tools/fax-management' },
    {
      label: 'Unmatched Voicemails',
      url: '/tools/voicemail-management',
      visible: () => viewUnmatchedVoicemails.value,
    },
    {
      label: 'Bulk Texting',
      items: [
        { label: 'Campaigns', url: '/tools/campaigns' },
        { label: 'Create Cohort', url: '/tools/cohorts' },
      ],
    },
    { label: 'Bulk Edit Subtasks', url: '/tools/bulk_edit' },
    {
      label: 'Humana Eligibility Check',
      url: '/tools/humana-eligibility-check',
    },
    { label: 'Non-Member Callbacks', url: '/tools/provider-callbacks' },
    {
      label: 'Form Responses',
      items: [
        { label: 'Form Response Viewer', url: '/tools/form-response' },
        {
          label: 'Search Form Responses',
          url: '/tools/search-form-responses',
        },
      ],
    },
  ]

  const items = computed<MenuItem[]>(() => {
    return applyVisibilityDefaults(
      [
        { label: 'Lead Queue', url: '/pod-lead-queue' },
        { label: 'Enrollment Lead Queue', url: '/enrollment-lead-queue' },
        { label: 'My Queue', url: '/my-queue' },
        { label: 'Enrollment Queue', url: '/enrollment-queue' },
        { label: 'Scheduled Calls', url: '/scheduled-calls' },
        { label: 'Enrollment Queue (new)', url: '/omaat-enrollment-queue' },
        { label: 'Members', url: '/patients' },
        { label: 'Tasks', url: '/tasks/all-tasks' },
        { label: 'Admin', items: adminItems },
        { label: 'Tools', items: toolsItems },
      ],
      checkIfRouteAuthorized
    )
  })
  return { items }
}
